import { useState } from 'react';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Download, Copy, Check, Palette, Type, Layout as LayoutIcon, Layers, MousePointer } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { toast } from 'sonner';

const colorTokens = [
  { name: '--dsh-primary',       value: 'hsl(24 100% 50%)',  hex: '#FF6B00', label: 'Primary / Orange' },
  { name: '--dsh-primary-dark',  value: 'hsl(24 100% 45%)',  hex: '#E56000', label: 'Primary Dark (AA)' },
  { name: '--dsh-accent',        value: 'hsl(187 100% 45%)', hex: '#00CFDB', label: 'Accent / Teal' },
  { name: '--dsh-success',       value: 'hsl(142 70% 45%)',  hex: '#22C55E', label: 'Success' },
  { name: '--dsh-warning',       value: 'hsl(45 96% 50%)',   hex: '#F59E0B', label: 'Warning' },
  { name: '--dsh-error',         value: 'hsl(0 84% 60%)',    hex: '#EF4444', label: 'Error' },
  { name: '--dsh-info',          value: 'hsl(210 100% 56%)', hex: '#3B82F6', label: 'Info' },
];

const darkBgTokens = [
  { name: '--dsh-bg',           value: 'hsl(0 0% 2%)',  hex: '#050505', label: 'Background' },
  { name: '--dsh-bg-card',      value: 'hsl(0 0% 7%)',  hex: '#111111', label: 'Card' },
  { name: '--dsh-bg-elevated',  value: 'hsl(0 0% 9%)',  hex: '#171717', label: 'Elevated' },
  { name: '--dsh-bg-muted',     value: 'hsl(0 0% 15%)', hex: '#262626', label: 'Muted' },
  { name: '--dsh-border',       value: 'hsl(0 0% 15%)', hex: '#262626', label: 'Border' },
  { name: '--dsh-fg',           value: 'hsl(0 0% 93%)', hex: '#EDEDED', label: 'Foreground' },
  { name: '--dsh-fg-muted',     value: 'hsl(0 0% 64%)', hex: '#A3A3A3', label: 'Muted Text' },
];

const Section = ({ id, icon: Icon, title, children }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4 }}
    className="mb-16"
  >
    <div className="flex items-center gap-3 mb-8 pb-4 border-b border-border">
      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <h2 className="text-xl font-bold">{title}</h2>
    </div>
    {children}
  </motion.section>
);

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="p-1.5 rounded hover:bg-primary/10 transition-colors text-muted-foreground hover:text-primary">
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function ColorSwatch({ token }) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className="w-full h-16 rounded-xl border border-border/50 shadow-sm"
        style={{ backgroundColor: token.hex }}
      />
      <div>
        <p className="text-xs font-bold">{token.label}</p>
        <div className="flex items-center justify-between mt-0.5">
          <code className="text-xs text-muted-foreground font-mono">{token.hex}</code>
          <CopyButton text={token.name} />
        </div>
        <code className="text-xs text-muted-foreground/60 font-mono">{token.name}</code>
      </div>
    </div>
  );
}

export default function DesignSystemPage() {
  const { t } = useTranslation();

  const downloadCSS = () => {
    const link = document.createElement('a');
    link.href = '/dsh-design-tokens.css';
    link.download = 'dsh-design-tokens.css';
    link.click();
    toast.success('CSS-Datei wird heruntergeladen…');
  };

  return (
    <Layout>
      <div className="section-spacing">
        <div className="container-main">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
              <div>
                <Badge className="mb-3 bg-primary/10 text-primary border-primary/20">Design System</Badge>
                <h1 className="text-4xl md:text-5xl font-black mb-3">
                  DSH <span className="text-gradient">Design Tokens</span>
                </h1>
                <p className="text-muted-foreground max-w-xl">
                  Alle Design-Variablen, Farben, Typografie und Komponenten als portables CSS –
                  direkt in WHMCS, eigene Portale oder andere Anwendungen einbindbar.
                </p>
              </div>
              <div className="flex gap-3 shrink-0">
                <Button
                  onClick={downloadCSS}
                  className="bg-primary hover:bg-primary/90 glow-hover font-bold"
                  data-testid="download-css-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  CSS herunterladen
                </Button>
                <a href="/whmcs-guide" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-primary/30 hover:border-primary">
                    WHMCS-Anleitung
                  </Button>
                </a>
                <a href="/whmcs-preview" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-primary/30 hover:border-primary">
                    WHMCS-Vorschau
                  </Button>
                </a>
              </div>
            </div>

            {/* Quick nav */}
            <div className="flex flex-wrap gap-2 mt-8">
              {[
                { id: 'colors', label: 'Farben' },
                { id: 'typography', label: 'Typografie' },
                { id: 'buttons', label: 'Buttons' },
                { id: 'badges', label: 'Badges' },
                { id: 'cards', label: 'Cards' },
                { id: 'inputs', label: 'Inputs' },
                { id: 'status', label: 'Status & Alerts' },
                { id: 'usage', label: 'Integration' },
              ].map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="text-xs font-medium px-3 py-1.5 rounded-full glass border border-border hover:border-primary/40 hover:text-primary transition-colors"
                >
                  {label}
                </a>
              ))}
            </div>
          </motion.div>

          {/* ── Colors ── */}
          <Section id="colors" icon={Palette} title="Farben">
            <div className="space-y-8">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Brand Colors</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                  {colorTokens.map(t => <ColorSwatch key={t.name} token={t} />)}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Dark Mode Backgrounds</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                  {darkBgTokens.map(t => <ColorSwatch key={t.name} token={t} />)}
                </div>
              </div>
            </div>
          </Section>

          {/* ── Typography ── */}
          <Section id="typography" icon={Type} title="Typografie">
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { font: 'Outfit', role: 'Headings', weight: 'font-black', varName: '--dsh-font-heading', sample: 'DeinServerHost' },
                { font: 'DM Sans', role: 'Body', weight: 'font-normal', varName: '--dsh-font-body', sample: 'Schnell. Zuverlässig. PrePaid.' },
                { font: 'JetBrains Mono', role: 'Code / Mono', weight: 'font-normal', varName: '--dsh-font-mono', sample: '192.168.1.1' },
              ].map(item => (
                <div key={item.font} className="glass rounded-2xl border border-border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="secondary" className="text-xs">{item.role}</Badge>
                    <CopyButton text={`font-family: var(${item.varName});`} />
                  </div>
                  <p
                    className={`text-3xl mb-3 ${item.weight}`}
                    style={{ fontFamily: item.font === 'JetBrains Mono' ? 'monospace' : item.font === 'Outfit' ? 'Outfit, sans-serif' : 'DM Sans, sans-serif' }}
                  >
                    {item.sample}
                  </p>
                  <code className="text-xs text-muted-foreground">var({item.varName})</code>
                </div>
              ))}
            </div>
            <div className="mt-6 glass rounded-2xl border border-border p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Größen-Skala</h3>
              <div className="space-y-2">
                {[
                  { size: 'text-xs',   px: '12px', token: '--dsh-text-xs',   sample: 'Extra Small – Labels, Captions' },
                  { size: 'text-sm',   px: '14px', token: '--dsh-text-sm',   sample: 'Small – Body Text, Cards' },
                  { size: 'text-base', px: '16px', token: '--dsh-text-base', sample: 'Base – Default Paragraph' },
                  { size: 'text-lg',   px: '18px', token: '--dsh-text-lg',   sample: 'Large – Section Sub-Headers' },
                  { size: 'text-2xl',  px: '24px', token: '--dsh-text-2xl',  sample: 'H3 – Card Titles' },
                  { size: 'text-4xl',  px: '36px', token: '--dsh-text-4xl',  sample: 'H1 – Page Titles' },
                ].map(item => (
                  <div key={item.size} className="flex items-baseline gap-4 py-1.5 border-b border-border last:border-0">
                    <code className="text-xs text-muted-foreground w-32 shrink-0">{item.token}</code>
                    <span className={`${item.size} font-medium`}>{item.sample}</span>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          {/* ── Buttons ── */}
          <Section id="buttons" icon={MousePointer} title="Buttons">
            <div className="glass rounded-2xl border border-border p-8 space-y-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">Varianten</p>
                <div className="flex flex-wrap gap-3">
                  <Button className="bg-primary hover:bg-primary/90 glow-hover font-bold uppercase tracking-wider">Primary</Button>
                  <Button variant="outline" className="border-primary/30 hover:border-primary font-bold uppercase tracking-wider">Outline</Button>
                  <Button variant="secondary" className="font-bold uppercase tracking-wider">Secondary</Button>
                  <Button variant="ghost" className="font-bold uppercase tracking-wider">Ghost</Button>
                  <Button className="bg-destructive hover:bg-destructive/90 font-bold uppercase tracking-wider">Destructive</Button>
                  <Button className="bg-green-600 hover:bg-green-700 font-bold uppercase tracking-wider text-white">Success</Button>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">Größen</p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button size="sm" className="bg-primary hover:bg-primary/90 font-bold">Small</Button>
                  <Button className="bg-primary hover:bg-primary/90 font-bold">Default</Button>
                  <Button size="lg" className="bg-primary hover:bg-primary/90 font-bold">Large</Button>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">States</p>
                <div className="flex flex-wrap gap-3">
                  <Button className="bg-primary hover:bg-primary/90 font-bold" disabled>Disabled</Button>
                  <Button className="bg-primary hover:bg-primary/90 font-bold" disabled>
                    <span className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Loading
                  </Button>
                </div>
              </div>
            </div>
          </Section>

          {/* ── Badges ── */}
          <Section id="badges" icon={Layers} title="Badges & Status-Indikatoren">
            <div className="glass rounded-2xl border border-border p-8">
              <div className="flex flex-wrap gap-3">
                <Badge className="bg-primary/10 text-primary border border-primary/20 text-xs font-bold uppercase tracking-wider">Primary</Badge>
                <Badge className="bg-green-500/10 text-green-500 border border-green-500/20 text-xs font-bold uppercase tracking-wider">Online</Badge>
                <Badge className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 text-xs font-bold uppercase tracking-wider">Warning</Badge>
                <Badge className="bg-red-500/10 text-red-500 border border-red-500/20 text-xs font-bold uppercase tracking-wider">Stopped</Badge>
                <Badge className="bg-blue-500/10 text-blue-500 border border-blue-500/20 text-xs font-bold uppercase tracking-wider">Info</Badge>
                <Badge variant="secondary" className="text-xs font-bold uppercase tracking-wider">Muted</Badge>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <div className="flex items-center gap-2 px-3 py-1 rounded-full glass border border-border text-sm">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  Running
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full glass border border-border text-sm">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  Stopped
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full glass border border-border text-sm">
                  <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                  Rebooting
                </div>
              </div>
            </div>
          </Section>

          {/* ── Cards ── */}
          <Section id="cards" icon={LayoutIcon} title="Cards">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="rounded-2xl border border-border bg-card p-6">
                <Badge className="text-xs mb-3 bg-primary/10 text-primary border-primary/20">Standard Card</Badge>
                <h3 className="text-lg font-bold mb-2">KVM VPS Ryzen</h3>
                <p className="text-sm text-muted-foreground">Moderne AMD Ryzen Prozessoren für maximale Single-Core Performance.</p>
              </div>
              <div className="glass rounded-2xl border border-border p-6">
                <Badge className="text-xs mb-3 bg-primary/10 text-primary border-primary/20">Glass Card</Badge>
                <h3 className="text-lg font-bold mb-2">KVM VPS Standard</h3>
                <p className="text-sm text-muted-foreground">Leistungsstarke VPS auf Intel Xeon Basis mit NVMe-Speicher.</p>
              </div>
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 shadow-lg shadow-primary/10">
                <Badge className="text-xs mb-3 bg-primary text-white">Highlighted Card</Badge>
                <h3 className="text-lg font-bold mb-2">KVM VPS Windows</h3>
                <p className="text-sm text-muted-foreground">Windows Server Lizenzen inklusive – sofort einsatzbereit.</p>
              </div>
            </div>
          </Section>

          {/* ── Inputs ── */}
          <Section id="inputs" icon={Type} title="Inputs & Formulare">
            <div className="glass rounded-2xl border border-border p-8 grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">Standard Input</label>
                  <Input placeholder="Servername oder IP…" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">Disabled</label>
                  <Input placeholder="Nicht bearbeitbar" disabled />
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">Mit Icon-Prefix</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-mono">&gt;_</span>
                    <Input className="pl-8 font-mono" placeholder="apt-get update" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">Error State</label>
                  <Input placeholder="Ungültige IP-Adresse" className="border-red-500/50 focus:border-red-500" />
                  <p className="text-xs text-red-500 mt-1">Bitte eine gültige IP-Adresse eingeben.</p>
                </div>
              </div>
            </div>
          </Section>

          {/* ── Status & Alerts ── */}
          <Section id="status" icon={Layers} title="Status & Alerts">
            <div className="space-y-3">
              {[
                { type: 'success', color: 'green', label: 'Erfolg', msg: 'Der Server wurde erfolgreich gestartet.' },
                { type: 'warning', color: 'yellow', label: 'Warnung', msg: 'Die Server-Ressourcen sind zu 80% ausgelastet.' },
                { type: 'error',   color: 'red',    label: 'Fehler',  msg: 'Verbindung zum Server konnte nicht hergestellt werden.' },
                { type: 'info',    color: 'blue',   label: 'Info',    msg: 'Das Backup wird automatisch um 02:00 Uhr erstellt.' },
              ].map(item => (
                <div
                  key={item.type}
                  className={`flex items-start gap-3 p-4 rounded-xl border text-sm`}
                  style={{
                    backgroundColor: `var(--dsh-${item.type}-alpha, hsla(${item.type === 'success' ? '142,70%,45%' : item.type === 'warning' ? '45,96%,50%' : item.type === 'error' ? '0,84%,60%' : '210,100%,56%'}, 0.08))`,
                    borderColor: `hsla(${item.type === 'success' ? '142,70%,45%' : item.type === 'warning' ? '45,96%,50%' : item.type === 'error' ? '0,84%,60%' : '210,100%,56%'}, 0.3)`,
                  }}
                >
                  <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                    item.type === 'success' ? 'bg-green-500' : item.type === 'warning' ? 'bg-yellow-500' : item.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
                  }`} />
                  <div>
                    <strong className={`font-bold ${
                      item.type === 'success' ? 'text-green-500' : item.type === 'warning' ? 'text-yellow-500' : item.type === 'error' ? 'text-red-500' : 'text-blue-400'
                    }`}>{item.label}:</strong>
                    <span className="text-muted-foreground ml-1">{item.msg}</span>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* ── Integration ── */}
          <Section id="usage" icon={Download} title="Integration in WHMCS">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass rounded-2xl border border-border p-6">
                <h3 className="font-bold mb-3">1. CSS einbinden</h3>
                <div className="relative rounded-lg overflow-hidden bg-muted p-4 font-mono text-xs">
                  <code className="text-muted-foreground whitespace-pre">{`<!-- In deiner WHMCS Vorlage (header.tpl): -->
<link rel="stylesheet" href="/dsh-design-tokens.css">

<!-- Google Fonts für korrekte Typografie: -->
<link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&family=DM+Sans:wght@400;500;700&display=swap" rel="stylesheet">`}</code>
                </div>
              </div>
              <div className="glass rounded-2xl border border-border p-6">
                <h3 className="font-bold mb-3">2. Klassen verwenden</h3>
                <div className="relative rounded-lg overflow-hidden bg-muted p-4 font-mono text-xs">
                  <code className="text-muted-foreground whitespace-pre">{`<!-- Container -->
<div class="dsh-root dsh-dark dsh-portal-layout">

  <!-- Button -->
  <button class="dsh-btn dsh-btn-primary">
    Server starten
  </button>

  <!-- Badge -->
  <span class="dsh-badge dsh-badge-success">
    Online
  </span>

  <!-- Card -->
  <div class="dsh-card">...</div>
</div>`}</code>
                </div>
              </div>
              <div className="glass rounded-2xl border border-border p-6 md:col-span-2">
                <h3 className="font-bold mb-3">3. CSS-Variablen direkt nutzen</h3>
                <div className="rounded-lg overflow-hidden bg-muted p-4 font-mono text-xs">
                  <code className="text-muted-foreground whitespace-pre">{`.my-custom-element {
  color:            var(--dsh-primary);
  background:       var(--dsh-bg-card);
  border:           1px solid var(--dsh-border);
  border-radius:    var(--dsh-radius-xl);
  font-family:      var(--dsh-font-heading);
  box-shadow:       var(--dsh-glow);
}`}</code>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-center">
              <Button
                onClick={downloadCSS}
                size="lg"
                className="bg-primary hover:bg-primary/90 glow-hover font-bold"
                data-testid="download-css-btn-bottom"
              >
                <Download className="w-5 h-5 mr-2" />
                dsh-design-tokens.css herunterladen
              </Button>
            </div>
          </Section>

        </div>
      </div>
    </Layout>
  );
}
